import React, { useContext, useEffect, useState } from 'react';
import Table from 'react-bootstrap/Table';
import ProductDetailModal from './ProductDetailModal';
import { defaultImageUrl } from '../../config_and_helpers/config';
import {
  calculatePricePerSubUnit,
  calculatePricePerUnit,
  getFinalProductPrice,
  sortProducts,
} from '../../config_and_helpers/helpers';
import NavigateToUrlIcon from '../Generic/NavigateToUrlIcon';
import PriceDifferenceComponent from '../Generic/PriceDifferenceComponent';
import ProductPriceDisplay from './ProductPriceDisplay';
import CustomProductContextHook from '../../pages/Product/CustomProductContextHook';
import { fetchCurrencies } from '../../clients/currency_client';
import { AppContext } from '../../App';
import S3MediaImage from '../Generic/S3MediaImage';
import S3MediaLink from '../Generic/S3MediaLink';

/**
 * Render a table displaying similar products.
 *
 * @param {Object} props - The component props.
 * @param {Array} props.similarProducts - The list of similar products.
 * @param {Object} props.baseProduct - The base product.
 * @returns {JSX.Element} - The rendered table component.
 */
const SimilarProductsTable = ({ similarProducts, baseProduct }) => {
  // Import required hooks and context
  const { token } = useContext(AppContext); // Get the token from AppContext for server communication
  const context = CustomProductContextHook(); // Try to retrieve product context (might be missing)

  // Local fallback state for currency rates if context doesn't provide it
  const [localCurrencyRates, setLocalCurrencyRates] = useState(null);

  // Decide whether to use context currencyRates or fallback to local state
  const currencyRates = context?.currencyRates || localCurrencyRates;
  const setCurrencyRates = context?.setCurrencyRates || setLocalCurrencyRates;

  // Sort similar products by preferred criteria (e.g., by name or price)
  const sortedSimilarProducts = sortProducts(similarProducts);

  // Check if base product has a discount
  const calculateDiscount = baseProduct?.discount !== null;

  // Fetch currency rates manually only if missing
  useEffect(() => {
    if (!currencyRates || currencyRates.length === 0) {
      fetchCurrencies(token).then(response => {
        if (response.success) {
          setCurrencyRates(response.data); // Update either context or local fallback
        }
      });
    }
  }, [currencyRates, token, setCurrencyRates]);

  // If still no currency rates, show a loading indicator
  if (!currencyRates?.length) return <div>Loading rates...</div>;

  // Calculate the final price for the base product
  const baseProductPrice = getFinalProductPrice(
    baseProduct,
    currencyRates,
    calculateDiscount
  );

  // Calculate price per unit or per sub-unit depending on product structure
  const baseProductPricePerUnit =
    baseProduct?.units_per_sub_unit === null // Checks if there are no sub-units defined
      ? calculatePricePerUnit(baseProductPrice, baseProduct.unit_quantity) // Price per unit
      : calculatePricePerSubUnit(
          baseProductPrice,
          baseProduct.unit_quantity,
          baseProduct?.units_per_sub_unit // Price per sub-unit
        );

  return (
    <div className="container mt-4">
      <h2>Similar Items</h2>
      <Table striped bordered hover>
        <thead>
          <tr className="text-center align-middle">
            <th>#</th>
            <th></th>
            <th>Name</th>
            <th>Catalog Number</th>
            <th>Price</th>
            <th>Product Profile</th>
          </tr>
        </thead>
        <tbody>
          {sortedSimilarProducts.map((similarProduct, index) => {
            // Determine if the similarProduct has a discount that needs to be calculated
            const calculateDiscount = similarProduct?.discount !== null;

            // Calculate the similarProduct retail price, prior discounts
            const productRetailConvertedPrice = getFinalProductPrice(
              similarProduct,
              currencyRates
            );

            // Calculate the similarProduct final price, after discounts
            const productDiscountedConvertedPrice = getFinalProductPrice(
              similarProduct,
              currencyRates,
              calculateDiscount
            );

            //   Determine the price of the compared similar item to the similarProduct currently being viewed
            const similarProductPrice =
              similarProduct && similarProduct?.price
                ? getFinalProductPrice(
                    similarProduct,
                    currencyRates,
                    calculateDiscount
                  )
                : similarProduct?.price;

            // Determine if the product has subunits
            const productHasSubUnits =
              similarProduct?.units_per_sub_unit !== null;

            // Calculate the product's price per smallest unit
            const pricePerSmallestUnit = productHasSubUnits
              ? calculatePricePerSubUnit(
                  productDiscountedConvertedPrice,
                  similarProduct.unit_quantity,
                  similarProduct?.units_per_sub_unit
                )
              : calculatePricePerUnit(
                  productDiscountedConvertedPrice,
                  similarProduct.unit_quantity
                );

            return (
              <tr key={similarProduct.id} className="text-center align-middle">
                <td>{index + 1}</td>
                <td>
                  {similarProduct?.image_url ? (
                    <S3MediaLink
                      url={similarProduct.image_url}
                      title="Open product image"
                    >
                      <S3MediaImage
                        url={similarProduct.image_url}
                        fallbackUrl={defaultImageUrl}
                        style={{ width: '150px', height: '150px' }}
                        alt={similarProduct.name || 'Product'}
                      />
                    </S3MediaLink>
                  ) : (
                    <img
                      style={{ width: '150px', height: '150px' }}
                      src={defaultImageUrl}
                      alt={similarProduct.name || 'Product'}
                    />
                  )}
                </td>
                <td>
                  <ProductDetailModal productObj={similarProduct} />
                </td>
                <td>{similarProduct.cat_num}</td>
                <td style={{ minWidth: '200px', height: '180px' }}>
                  <div className="text-center align-middle">
                    <ProductPriceDisplay
                      product={similarProduct}
                      productDiscountedConvertedPrice={
                        productDiscountedConvertedPrice
                      }
                      productRetailConvertedPrice={productRetailConvertedPrice}
                      pricePerSmallestUnit={pricePerSmallestUnit}
                      currencyRates={currencyRates}
                      productHasSubUnits={productHasSubUnits}
                    />
                    {baseProduct.price && similarProductPrice && (
                      <div className="d-flex justify-content-center mt-3">
                        <PriceDifferenceComponent
                          baseProductPricePerUnit={baseProductPricePerUnit}
                          comparedPricePerUnit={pricePerSmallestUnit}
                          productHasSubUnits={productHasSubUnits}
                          productUnit={similarProduct.unit}
                        />
                      </div>
                    )}
                  </div>
                </td>
                <td style={{ width: '80px' }}>
                  {similarProduct.url && (
                    <a
                      href={similarProduct.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="d-flex justify-content-center align-items-center"
                    >
                      <NavigateToUrlIcon iconSize={'2rem'} />
                    </a>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};
export default SimilarProductsTable;
